import React from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { AutoFetchButton } from '@core/Components/AutoFetchButton'
import { getBetLoopSharedBetsFeed } from '@core/Areas/BetLoop/helpers/api'
import type { BetLoopFeedParams } from '@core/Areas/BetLoop/helpers/api/types'
import { BetLoopFeedPageSize } from '@core/Areas/BetLoop/constants'
import { useSharedBetsTopicSubscription } from '@core/Areas/BetLoop/hooks/useSharedBetsTopicSubscription'

export const useSharedBetsData = ({
  feedType = 'explore',
  sortOrder,
  sortFilter,
  accountId = null,
  pageSize,
}: Pick<BetLoopFeedParams, 'feedType' | 'sortOrder' | 'sortFilter' | 'accountId' | 'pageSize'>) => {
  useSharedBetsTopicSubscription()

  const { data, isLoading, refetch, isRefetching, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [
        ...queryKeys.betLoopSharedBetsFeedBase(feedType),
        sortOrder,
        sortFilter,
        accountId,
        pageSize,
      ],

      queryFn: ({ pageParam }: { pageParam: number | null }) =>
        getBetLoopSharedBetsFeed({
          feedType,
          pageParam,
          sortOrder,
          sortFilter,
          accountId,
          pageSize: pageSize ?? BetLoopFeedPageSize,
        }),

      initialPageParam: null,

      getNextPageParam: lastPage => {
        if (!lastPage || lastPage.length < Number(pageSize ?? BetLoopFeedPageSize)) return undefined
        return lastPage[lastPage.length - 1]?.postId ?? undefined
      },

      staleTime: 1_000 * 60 * 2, // 2min
      gcTime: 1_000 * 60 * 5, // 5min
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
    })

  const isEmptyState = data?.pages[0].length === 0
  const isFinalPage =
    data && data.pages[data.pages.length - 1].length < (pageSize ?? BetLoopFeedPageSize)

  return {
    data,
    isLoading,
    refetch,
    isRefetching,
    /** Automatically triggers a fetch for next page when in view */
    LoadMoreButton: () =>
      isFinalPage || isEmptyState || isLoading ? null : (
        <AutoFetchButton onClick={fetchNextPage} isFetching={isFetchingNextPage} />
      ),
    isEmptyState,
  }
}
